import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const AwardsRecognition = () => {
  const awards = [
    {
      title: "Best Authentic Mediterranean Restaurant",
      organization: "City Food Critics Association",
      year: "2023",
      description: "Recognized for outstanding commitment to traditional Mediterranean cuisine and exceptional customer service.",
      image: "https://images.unsplash.com/photo-1551218808-94e220e084d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Award",
      category: "Culinary Excellence"
    },
    {
      title: "Community Business of the Year",
      organization: "Chamber of Commerce",
      year: "2022",
      description: "Honored for significant contributions to local community development and supporting local businesses.",
      image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Users",
      category: "Community Impact"
    },
    {
      title: "Excellence in Traditional Cooking",
      organization: "Mediterranean Culinary Institute",
      year: "2021",
      description: "Awarded for preserving and promoting authentic Mediterranean cooking techniques and recipes.",
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "ChefHat",
      category: "Culinary Tradition"
    },
    {
      title: "Sustainable Business Practices Award",
      organization: "Green Business Alliance",
      year: "2021",
      description: "Recognized for implementing eco-friendly practices and supporting local sustainable agriculture.",
      image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Leaf",
      category: "Sustainability"
    },
    {
      title: "Customer Service Excellence",
      organization: "Restaurant Industry Association",
      year: "2020",
      description: "Honored for consistently delivering exceptional customer experiences and building lasting relationships.",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Heart",
      category: "Service Excellence"
    },
    {
      title: "Innovation in Traditional Cuisine",
      organization: "Culinary Arts Council",
      year: "2019",
      description: "Recognized for successfully blending traditional Mediterranean flavors with modern dining preferences.",
      image: "https://images.unsplash.com/photo-1544025162-d76694265947?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      icon: "Lightbulb",
      category: "Innovation"
    }
  ];

  const mediaFeatures = [
    {
      publication: "Food & Wine Magazine",
      title: "Hidden Gems: Authentic Mediterranean Cuisine",
      date: "March 2023",
      quote: "Kebab Palace delivers an authentic Mediterranean experience that transports diners straight to the coastal towns of Turkey and Greece.",
      image: "https://images.unsplash.com/photo-1586985289688-ca3cf47d3e6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      type: "Magazine Feature"
    },
    {
      publication: "Local Eats TV Show",
      title: "Family Traditions: Three Generations of Flavor",
      date: "August 2022",
      quote: "The Mahmoud family\'s dedication to preserving traditional cooking methods while embracing modern hospitality makes Kebab Palace a true community treasure.",
      image: "https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      type: "TV Feature"
    },
    {
      publication: "City Lifestyle Blog",
      title: "Where Tradition Meets Excellence",
      date: "November 2022",
      quote: "From the moment you walk in, you\'re treated like family. The authentic flavors and warm hospitality make every visit memorable.",
      image: "https://images.unsplash.com/photo-1432821596592-e2c18b78144f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      type: "Blog Review"
    },
    {
      publication: "Culinary Quarterly",
      title: "Preserving Mediterranean Heritage Through Food",
      date: "June 2021",
      quote: "Kebab Palace stands as a beacon of authentic Mediterranean cuisine, where every dish tells the story of generations of culinary expertise.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      type: "Magazine Article"
    }
  ];

  const customerRatings = [
    { platform: "Google Reviews", rating: 4.8, reviews: 1247, icon: "Star" },
    { platform: "Yelp", rating: 4.7, reviews: 892, icon: "MessageCircle" },
    { platform: "TripAdvisor", rating: 4.9, reviews: 634, icon: "MapPin" },
    { platform: "Facebook", rating: 4.8, reviews: 756, icon: "ThumbsUp" }
  ];

  return (
    <div className="py-8">
      <div className="text-center mb-16">
        <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-6">
          Awards & Recognition
        </h2>
        <p className="font-body text-lg text-text-secondary max-w-3xl mx-auto">
          Our commitment to authentic Mediterranean cuisine and exceptional service has been recognized by culinary experts, community organizations, and thousands of satisfied customers.
        </p>
      </div>

      {/* Awards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {awards.map((award, index) => (
          <motion.div
            key={award.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="card-surface hover:shadow-warm-lg transition-organic"
          >
            <div className="relative overflow-hidden rounded-lg mb-4">
              <Image
                src={award.image}
                alt={award.title}
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/60 to-transparent"></div>
              <div className="absolute top-4 right-4">
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center shadow-warm">
                  <Icon name={award.icon} size={24} className="text-white" />
                </div>
              </div>
              <div className="absolute bottom-4 left-4">
                <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-cta font-semibold text-primary">
                  {award.year}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <span className="text-xs font-cta font-semibold text-accent-600 uppercase tracking-wide">
                  {award.category}
                </span>
                <h3 className="font-headline text-xl font-bold text-text-primary mt-1">
                  {award.title}
                </h3>
                <p className="font-body text-secondary font-medium">
                  {award.organization}
                </p>
              </div>
              
              <p className="font-body text-text-secondary text-sm leading-relaxed">
                {award.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Customer Ratings */}
      <div className="mb-16">
        <h3 className="font-headline text-3xl font-bold text-text-primary text-center mb-12">
          Customer Reviews & Ratings
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {customerRatings.map((rating, index) => (
            <motion.div
              key={rating.platform}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card-surface text-center hover:shadow-warm-lg transition-organic"
            >
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={rating.icon} size={24} className="text-white" />
              </div>
              
              <h4 className="font-cta font-semibold text-text-primary mb-2">
                {rating.platform}
              </h4>
              
              <div className="flex items-center justify-center space-x-1 mb-2">
                <span className="font-headline text-2xl font-bold text-accent">
                  {rating.rating}
                </span>
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={16}
                      className={`${
                        i < Math.floor(rating.rating)
                          ? 'text-accent fill-current' :'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              <p className="text-sm text-text-secondary">
                Based on {rating.reviews.toLocaleString()} reviews
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Media Features */}
      <div className="mb-16">
        <h3 className="font-headline text-3xl font-bold text-text-primary text-center mb-12">
          Media Features & Press
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {mediaFeatures.map((feature, index) => (
            <motion.div
              key={feature.publication}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card-surface hover:shadow-warm-lg transition-organic"
            >
              <div className="flex gap-4">
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                  <Image
                    src={feature.image}
                    alt={feature.publication}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-cta font-semibold text-primary uppercase tracking-wide">
                      {feature.type}
                    </span>
                    <span className="text-xs text-text-secondary">
                      {feature.date}
                    </span>
                  </div>
                  
                  <h4 className="font-cta font-semibold text-text-primary mb-1">
                    {feature.publication}
                  </h4>
                  
                  <h5 className="font-body text-sm text-secondary mb-3">
                    {feature.title}
                  </h5>
                  
                  <blockquote className="font-accent italic text-text-secondary text-sm leading-relaxed">
                    "{feature.quote}"
                  </blockquote>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recognition Summary */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <div className="max-w-4xl mx-auto card-surface bg-gradient-to-r from-accent-50 to-primary-50">
          <Icon name="Trophy" size={48} className="text-accent mx-auto mb-6" />
          <h3 className="font-headline text-3xl font-bold text-text-primary mb-6">
            Recognition Built on Excellence
          </h3>
          <p className="font-body text-lg text-text-secondary leading-relaxed mb-8">
            These awards and recognitions represent more than accolades – they reflect our unwavering commitment to authentic Mediterranean cuisine, exceptional service, and meaningful community connections. Every recognition motivates us to continue exceeding expectations and preserving culinary traditions for future generations.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="font-headline text-3xl font-bold text-accent mb-2">15+</div>
              <p className="font-body text-text-secondary">Awards & Recognitions</p>
            </div>
            <div className="text-center">
              <div className="font-headline text-3xl font-bold text-primary mb-2">4.8</div>
              <p className="font-body text-text-secondary">Average Rating</p>
            </div>
            <div className="text-center">
              <div className="font-headline text-3xl font-bold text-secondary mb-2">3,500+</div>
              <p className="font-body text-text-secondary">Customer Reviews</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AwardsRecognition;